"use client";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Skeleton,
  Textarea,
} from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  CircleDotIcon,
  ImageIcon,
  LandmarkIcon,
  StoreIcon,
  UserIcon,
  WrenchIcon,
  XCircleIcon,
} from "lucide-react";
import { useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import type { AuditItem } from "@/modules/audit/presentation/api/client";
import { RequirePermissions } from "@/modules/auth/presentation/ui/RequirePermissions";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import { formatPrice } from "@/shared/lib/format-price";
import { AppImage, resolveImageSrc } from "@/shared/ui/AppImage";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import {
  useApproveTopupMutation,
  useRejectTopupMutation,
  useTopupAuditQuery,
  useTopupDetailQuery,
} from "../api/topup-queries";

const STATUS_STYLE: Record<
  string,
  { dot: string; text: string; label: string }
> = {
  submitted: {
    dot: "bg-primary",
    text: "text-primary",
    label: "ລໍຖ້າອະນຸມັດ",
  },
  approved: {
    dot: "bg-secondary",
    text: "text-secondary",
    label: "ອະນຸມັດແລ້ວ",
  },
  posted: {
    dot: "bg-success",
    text: "text-success",
    label: "ສຳເລັດ",
  },
  rejected: {
    dot: "bg-destructive",
    text: "text-destructive",
    label: "ປະຕິເສດ",
  },
};

const OWNER_TYPE_LABELS: Record<string, string> = {
  customer: "ລູກຄ້າ",
  shop: "ຮ້ານຄ້າ",
  mechanic: "ຊ່າງ",
  platform: "ແພລດຟອມ",
};

const METHOD_LABELS: Record<string, string> = {
  transfer: "ໂອນເງິນ",
  provider: "ຜູ້ໃຫ້ບໍລິການ",
};

function OwnerIcon({ type }: { type: string }) {
  switch (type) {
    case "customer":
      return <UserIcon className="size-5 text-secondary" />;
    case "shop":
      return <StoreIcon className="size-5 text-secondary" />;
    case "mechanic":
      return <WrenchIcon className="size-5 text-secondary" />;
    case "platform":
      return <LandmarkIcon className="size-5 text-primary" />;
    default:
      return <UserIcon className="size-5 text-muted-foreground" />;
  }
}

export function TopupDetailPage() {
  const { id } = useParams({ from: "/app/wallet/topup/$id" });
  const nav = useNavigate();
  const { data: detail, isLoading } = useTopupDetailQuery(id);
  const approveMutation = useApproveTopupMutation(id);
  const rejectMutation = useRejectTopupMutation(id);
  const [rejectReason, setRejectReason] = useState("");

  const headerBreadcrumb = (
    <div className="flex items-center gap-3">
      <Breadcrumb
        items={[
          { label: "ກະເປົາເງິນ & ການຈ່າຍເງິນ", to: "/app/wallet" },
          { label: "ຄຳຂໍເຕີມເງິນ", to: "/app/wallet/topup" },
          {
            label: detail
              ? `#${detail.id.slice(0, 8).toUpperCase()}`
              : "ລາຍລະອຽດ",
          },
        ]}
      />
    </div>
  );

  if (isLoading) {
    return (
      <>
        <Header>{headerBreadcrumb}</Header>
        <Main>
          <div className="space-y-4">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-[300px] rounded-xl" />
          </div>
        </Main>
      </>
    );
  }

  if (!detail) {
    return (
      <>
        <Header>{headerBreadcrumb}</Header>
        <Main>
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-destructive">
            ບໍ່ພົບຄຳຂໍເຕີມເງິນ ຫຼື ເກີດຂໍ້ຜິດພາດ
          </div>
        </Main>
      </>
    );
  }

  const status = STATUS_STYLE[detail.status] ?? {
    dot: "bg-muted-foreground",
    text: "text-muted-foreground",
    label: detail.status,
  };

  const isSubmitted = detail.status === "submitted";

  const STATUS_BADGE_VARIANT: Record<
    string,
    "default" | "secondary" | "outline" | "destructive"
  > = {
    submitted: "default",
    approved: "secondary",
    posted: "outline",
    rejected: "destructive",
  };

  return (
    <>
      <Header>{headerBreadcrumb}</Header>

      <Main>
        {/* Toolbar */}
        <div className="mb-2 flex flex-wrap items-center justify-between space-y-2">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="font-bold text-2xl tracking-tight">ຄຳຂໍເຕີມເງິນ</h2>
              <Badge variant={STATUS_BADGE_VARIANT[detail.status] ?? "outline"}>
                {status.label}
              </Badge>
            </div>
            <p className="text-muted-foreground text-sm">ລະຫັດ: {detail.id}</p>
          </div>

          <Button
            variant="outline"
            onClick={() => nav({ to: "/app/wallet/topup" })}
          >
            <ArrowLeftIcon className="h-4 w-4" />
            ກັບຄືນ
          </Button>
        </div>

        <div className="grid gap-4 lg:grid-cols-[1fr_400px]">
          {/* Left: Slip image (large) */}
          <Card className="rounded-xl border-border">
            <CardHeader>
              <CardTitle className="text-lg">ຫຼັກຖານການໂອນ</CardTitle>
            </CardHeader>
            <CardContent>
              {detail.slipUrl ? (
                <div className="overflow-hidden rounded-lg border bg-muted/20">
                  <AppImage
                    src={detail.slipUrl}
                    alt="ສລິບການໂອນ"
                    className="max-h-[600px] w-full"
                    fit="contain"
                  />
                </div>
              ) : (
                <div className="flex h-64 flex-col items-center justify-center rounded-lg border border-dashed text-muted-foreground">
                  <ImageIcon className="mb-2 size-10" />
                  <p className="text-sm">ບໍ່ມີຫຼັກຖານການໂອນ</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Right column: Info + Audit */}
          <div className="flex flex-col gap-4">
            {/* Info Card */}
            <Card className="rounded-xl border-border">
              <CardContent className="p-5">
                {/* Owner row + Timestamp */}
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    {detail.ownerImage ? (
                      <img
                        src={resolveImageSrc(detail.ownerImage) ?? ""}
                        alt={detail.ownerName ?? ""}
                        className="size-10 shrink-0 rounded-full object-cover"
                      />
                    ) : (
                      <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
                        <OwnerIcon type={detail.ownerType} />
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="truncate font-semibold text-sm">
                        {detail.ownerName ?? "—"}
                      </p>
                      <p className="text-muted-foreground text-xs">
                        {OWNER_TYPE_LABELS[detail.ownerType] ??
                          detail.ownerType}
                        {detail.ownerId
                          ? ` · ${detail.ownerId.slice(0, 10)}`
                          : ""}
                      </p>
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="text-muted-foreground text-xs uppercase tracking-wide">
                      ເວລາ
                    </p>
                    <p className="text-sm">
                      {formatDateTimeLocal(detail.createdAt)}
                    </p>
                  </div>
                </div>

                {/* Divider */}
                <div className="my-4 border-border/50 border-t" />

                {/* Amount (hero) */}
                <div>
                  <p className="text-muted-foreground text-xs uppercase tracking-wide">
                    ຈຳນວນເງິນ & ສະກຸນ
                  </p>
                  <p className="mt-1 font-bold text-3xl text-success tracking-tight">
                    {formatPrice(detail.amount, detail.currency, {
                      appendCurrency: false,
                    })}
                    <span className="ml-2 font-medium text-base text-muted-foreground">
                      {detail.currency}
                    </span>
                  </p>
                </div>

                {/* Divider */}
                <div className="my-4 border-border/50 border-t" />

                {/* Payment Method + Reference */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-muted-foreground text-xs uppercase tracking-wide">
                      ວິທີຊຳລະ
                    </p>
                    <div className="mt-1 flex items-center gap-2">
                      <LandmarkIcon className="size-4 text-muted-foreground" />
                      <span className="font-medium text-sm">
                        {METHOD_LABELS[detail.method] ?? detail.method}
                      </span>
                    </div>
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs uppercase tracking-wide">
                      Wallet ID
                    </p>
                    <p className="mt-1 font-mono text-sm">
                      {detail.walletId.slice(0, 12).toUpperCase()}
                    </p>
                  </div>
                </div>

                {detail.transferReference ? (
                  <div className="mt-4">
                    <p className="text-muted-foreground text-xs uppercase tracking-wide">
                      ເລກອ້າງອີງ / ເວລາໂອນ (ລູກຄ້າ)
                    </p>
                    <p className="mt-1 text-sm">{detail.transferReference}</p>
                  </div>
                ) : null}

                {/* Approved / rejected info */}
                {(detail.approvedAt ||
                  detail.approvedByName ||
                  detail.rejectReason) && (
                  <>
                    <div className="my-4 border-border/50 border-t" />
                    <div className="grid grid-cols-2 gap-4">
                      {detail.approvedAt && (
                        <div>
                          <p className="text-muted-foreground text-xs uppercase tracking-wide">
                            {detail.status === "rejected"
                              ? "ວັນທີດຳເນີນການ"
                              : "ວັນທີອະນຸມັດ"}
                          </p>
                          <p className="mt-1 text-sm">
                            {formatDateTimeLocal(detail.approvedAt)}
                          </p>
                        </div>
                      )}
                      {detail.approvedByName && (
                        <div>
                          <p className="text-muted-foreground text-xs uppercase tracking-wide">
                            {detail.status === "rejected"
                              ? "ດຳເນີນການໂດຍ"
                              : "ອະນຸມັດໂດຍ"}
                          </p>
                          <p className="mt-1 font-medium text-sm">
                            {detail.approvedByName}
                          </p>
                        </div>
                      )}
                    </div>
                    {detail.rejectReason && (
                      <div className="mt-4 rounded-lg border border-destructive/30 bg-destructive/5 p-3">
                        <p className="text-muted-foreground text-xs uppercase tracking-wide">
                          ເຫດຜົນການປະຕິເສດ
                        </p>
                        <p className="mt-1 text-sm">{detail.rejectReason}</p>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            {/* Audit Timeline */}
            <AuditTimeline entityId={id} />
          </div>
        </div>

        {/* Actions */}
        {isSubmitted && (
          <div className="mt-6 flex items-center gap-3">
            <RequirePermissions all={["top_up:approve"]}>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    className="bg-success hover:bg-success/90"
                    disabled={approveMutation.isPending}
                  >
                    <CheckCircleIcon className="mr-2 size-4" />
                    ອະນຸມັດ
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>ຢືນຢັນການອະນຸມັດ</AlertDialogTitle>
                    <AlertDialogDescription>
                      ທ່ານແນ່ໃຈບໍ່ວ່າຕ້ອງການອະນຸມັດການເຕີມເງິນ{" "}
                      <strong>
                        {formatPrice(detail.amount, detail.currency)}
                      </strong>{" "}
                      ໃຫ້ <strong>{detail.ownerName}</strong>?
                      ຍອດເງິນຈະຖືກເພີ່ມເຂົ້າກະເປົາທັນທີ.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>ຍົກເລີກ</AlertDialogCancel>
                    <AlertDialogAction
                      className="bg-success hover:bg-success/90"
                      onClick={() => approveMutation.mutate()}
                      disabled={approveMutation.isPending}
                    >
                      ຢືນຢັນອະນຸມັດ
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </RequirePermissions>

            <RequirePermissions all={["top_up:reject"]}>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="destructive"
                    disabled={rejectMutation.isPending}
                  >
                    <XCircleIcon className="mr-2 size-4" />
                    ປະຕິເສດ
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>ຢືນຢັນການປະຕິເສດ</AlertDialogTitle>
                    <AlertDialogDescription>
                      ກະລຸນາລະບຸເຫດຜົນ (ບໍ່ບັງຄັບ)
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <Textarea
                    placeholder="ເຫດຜົນ..."
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    className="mt-2"
                  />
                  <AlertDialogFooter>
                    <AlertDialogCancel>ຍົກເລີກ</AlertDialogCancel>
                    <AlertDialogAction
                      className="bg-destructive hover:bg-destructive/90"
                      onClick={() => rejectMutation.mutate(rejectReason)}
                      disabled={rejectMutation.isPending}
                    >
                      ຢືນຢັນປະຕິເສດ
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </RequirePermissions>
          </div>
        )}
      </Main>
    </>
  );
}

// --- Audit Timeline ---

const ACTION_LABELS: Record<string, string> = {
  "topup.approve": "ອະນຸມັດການເຕີມເງິນ",
  "topup.reject": "ປະຕິເສດການເຕີມເງິນ",
  "topup.create": "ສ້າງຄຳຂໍເຕີມເງິນ",
};

const ACTION_COLORS: Record<string, { dot: string; line: string }> = {
  "topup.approve": { dot: "bg-success", line: "bg-success/30" },
  "topup.reject": { dot: "bg-destructive", line: "bg-destructive/30" },
};

const DEFAULT_COLOR = { dot: "bg-secondary", line: "bg-border" };

function AuditTimeline({ entityId }: { entityId: string }) {
  const { data, isLoading } = useTopupAuditQuery(entityId);

  if (isLoading) {
    return (
      <Card className="rounded-xl border-border">
        <CardHeader>
          <CardTitle className="text-lg">ບັນທຶກການດຳເນີນການ</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[1, 2].map((i) => (
              <div key={i} className="flex gap-3">
                <Skeleton className="size-6 shrink-0 rounded-full" />
                <div className="flex-1 space-y-1">
                  <Skeleton className="h-4 w-48" />
                  <Skeleton className="h-3 w-32" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const logs = data?.data ?? [];

  if (logs.length === 0) {
    return (
      <Card className="rounded-xl border-border">
        <CardHeader>
          <CardTitle className="text-lg">ບັນທຶກການດຳເນີນການ</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-sm">ຍັງບໍ່ມີບັນທຶກການດຳເນີນການ</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="rounded-xl border-border">
      <CardHeader>
        <CardTitle className="text-lg">ບັນທຶກການດຳເນີນການ</CardTitle>
      </CardHeader>
      <CardContent>
        <ol className="relative space-y-0">
          {logs.map((log, idx) => (
            <TimelineItem
              key={log.id}
              log={log}
              isLast={idx === logs.length - 1}
            />
          ))}
        </ol>
      </CardContent>
    </Card>
  );
}

function TimelineItem({ log, isLast }: { log: AuditItem; isLast: boolean }) {
  const colors = ACTION_COLORS[log.action] ?? DEFAULT_COLOR;
  const label = ACTION_LABELS[log.action] ?? log.action;
  const reason =
    log.meta && typeof log.meta === "object" && "reason" in log.meta
      ? String(log.meta.reason)
      : null;

  return (
    <li className="relative flex gap-4 pb-6 last:pb-0">
      {/* Vertical line */}
      {!isLast && (
        <span
          className={`absolute top-6 left-[11px] h-[calc(100%-12px)] w-0.5 ${colors.line}`}
          aria-hidden
        />
      )}

      {/* Dot */}
      <div className="relative z-10 flex shrink-0 items-start pt-0.5">
        <CircleDotIcon
          className={`size-6 rounded-full p-0.5 text-white ${colors.dot}`}
        />
      </div>

      {/* Content */}
      <div className="min-w-0 flex-1">
        <p className="font-medium text-sm">{label}</p>
        <p className="text-muted-foreground text-xs">
          {formatDateTimeLocal(log.occurredAt)}
        </p>
        {(log.actorName || log.actorId) && (
          <p className="mt-0.5 text-muted-foreground text-xs">
            ໂດຍ: {log.actorName ?? log.actorId}
          </p>
        )}

        {reason && (
          <p className="mt-1 rounded bg-muted/50 p-2 text-muted-foreground text-xs">
            ເຫດຜົນ: {reason}
          </p>
        )}
      </div>
    </li>
  );
}
